import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Loader2,
  ArrowLeft,
  Save,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Calendar,
  Stethoscope,
  Pill,
  Sun,
  Moon,
  FileText,
  UserCircle,
  ClipboardCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const responseConfig = {
  improved: { label: 'Improved', color: 'bg-success/10 text-success border-success/20', icon: TrendingUp, barColor: 'bg-success' },
  partial: { label: 'Partial', color: 'bg-warning/10 text-warning border-warning/20', icon: Minus, barColor: 'bg-warning' },
  none: { label: 'No Change', color: 'bg-muted text-muted-foreground border-border', icon: Minus, barColor: 'bg-muted-foreground' },
  worse: { label: 'Worse', color: 'bg-destructive/10 text-destructive border-destructive/20', icon: TrendingDown, barColor: 'bg-destructive' },
};

const adherenceConfig = {
  good: { label: 'Good', color: 'bg-success/10 text-success border-success/20' },
  partial: { label: 'Partial', color: 'bg-warning/10 text-warning border-warning/20' },
  poor: { label: 'Poor', color: 'bg-destructive/10 text-destructive border-destructive/20' },
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
}

function MedicineList({ medicines, label }) {
  if (!medicines || medicines.length === 0) return null;
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
      {medicines.map((med, i) => (
        <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/50 text-sm">
          <div>
            <span className="font-medium">{med.medicine_name}</span>
            {med.dosage && <span className="text-muted-foreground ml-2">— {med.dosage}</span>}
          </div>
          <div className="text-muted-foreground text-xs">
            {[med.frequency, med.duration].filter(Boolean).join(' · ')}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function FollowUpDetailPage() {
  const { followUpId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [followUp, setFollowUp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Edit form
  const [editForm, setEditForm] = useState({
    response: '',
    side_effects: '',
    adherence: '',
    notes: ''
  });

  const fetchFollowUp = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/follow-ups/${followUpId}`, { withCredentials: true });
      setFollowUp(data);
      setEditForm({
        response: data.response,
        side_effects: data.side_effects,
        adherence: data.adherence,
        notes: data.notes
      });
    } catch (error) {
      toast.error('Failed to load follow-up');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [followUpId, navigate]);

  useEffect(() => {
    fetchFollowUp();
  }, [fetchFollowUp]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/follow-ups/${followUpId}`, editForm, { withCredentials: true });
      toast.success('Follow-up updated');
      setIsEditing(false);
      fetchFollowUp();
    } catch (error) {
      toast.error('Failed to save follow-up');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="follow-up-detail-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!followUp) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Follow-up not found</p>
      </div>
    );
  }

  const rc = responseConfig[followUp.response] || responseConfig.none;
  const ac = adherenceConfig[followUp.adherence] || adherenceConfig.good;
  const RIcon = rc.icon;
  const prescription = followUp.prescription;
  const consultation = followUp.consultation;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="follow-up-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full" data-testid="back-button">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-3xl font-medium tracking-tight text-foreground">Follow-Up Details</h1>
          <p className="text-muted-foreground mt-1">{followUp.patient_name} · {formatDate(followUp.created_at)}</p>
        </div>
        {!isEditing ? (
          <Button onClick={() => setIsEditing(true)} variant="outline" className="rounded-full" data-testid="edit-follow-up-button">
            Edit
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button onClick={() => setIsEditing(false)} variant="outline" className="rounded-full">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 rounded-full" data-testid="save-follow-up-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        )}
      </div>

      {/* Outcome Summary */}
      <Card className="border-border shadow-sm overflow-hidden">
        <div className={`h-1.5 ${rc.barColor}`} />
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Response */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-muted/50 flex items-center justify-center mb-3">
                <RIcon className="w-8 h-8 text-primary" />
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Treatment Response</p>
              {isEditing ? (
                <Select value={editForm.response} onValueChange={(val) => setEditForm(p => ({ ...p, response: val }))}>
                  <SelectTrigger className="rounded-xl" data-testid="edit-response">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="improved">Improved</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="none">No Change</SelectItem>
                    <SelectItem value="worse">Worse</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Badge variant="outline" className={`text-base px-4 py-1 ${rc.color}`} data-testid="detail-response-badge">
                  {rc.label}
                </Badge>
              )}
            </div>

            {/* Adherence */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-muted/50 flex items-center justify-center mb-3">
                <ClipboardCheck className="w-8 h-8 text-primary" />
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Adherence</p>
              {isEditing ? (
                <Select value={editForm.adherence} onValueChange={(val) => setEditForm(p => ({ ...p, adherence: val }))}>
                  <SelectTrigger className="rounded-xl" data-testid="edit-adherence">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="poor">Poor</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Badge variant="outline" className={`text-base px-4 py-1 ${ac.color}`} data-testid="detail-adherence-badge">
                  {ac.label}
                </Badge>
              )}
            </div>

            {/* Side Effects */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-muted/50 flex items-center justify-center mb-3">
                <AlertTriangle className="w-8 h-8 text-primary" />
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Side Effects</p>
              {isEditing ? (
                <Textarea
                  value={editForm.side_effects}
                  onChange={(e) => setEditForm(p => ({ ...p, side_effects: e.target.value }))}
                  className="rounded-xl text-sm min-h-[60px]"
                  data-testid="edit-side-effects"
                />
              ) : (
                <p className="text-sm" data-testid="detail-side-effects">
                  {followUp.side_effects || 'None reported'}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card className="border-border shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Notes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <Textarea
              value={editForm.notes}
              onChange={(e) => setEditForm(p => ({ ...p, notes: e.target.value }))}
              className="rounded-xl min-h-[100px]"
              placeholder="Follow-up notes..."
              data-testid="edit-notes"
            />
          ) : (
            <p className="text-sm whitespace-pre-wrap" data-testid="detail-notes">
              {followUp.notes || 'No notes recorded.'}
            </p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Linked Consultation */}
        {consultation && (
          <Card className="border-border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2 text-info">
                <Stethoscope className="w-5 h-5" />
                Linked Consultation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="font-medium">{formatDate(consultation.consultation_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Type</p>
                  <p className="font-medium capitalize">{consultation.consultation_type}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Doctor</p>
                  <p className="font-medium">{consultation.doctor_name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <Badge variant="outline" className="capitalize">{consultation.status}</Badge>
                </div>
              </div>
              {consultation.diagnosis && (
                <div>
                  <p className="text-xs text-muted-foreground">Diagnosis</p>
                  <p className="text-sm">{consultation.diagnosis}</p>
                </div>
              )}
              {consultation.treatment_plan && (
                <div>
                  <p className="text-xs text-muted-foreground">Treatment Plan</p>
                  <p className="text-sm">{consultation.treatment_plan}</p>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                className="rounded-full mt-2"
                onClick={() => navigate(`/consultations/${consultation.id}`)}
                data-testid="view-consultation-button"
              >
                View Full Consultation
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Patient Info */}
        {followUp.patient && (
          <Card className="border-border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <UserCircle className="w-5 h-5" />
                Patient
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Name</p>
                  <p className="font-medium">{followUp.patient.name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Age / Gender</p>
                  <p className="font-medium">{followUp.patient.age} / {followUp.patient.gender}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Skin Type</p>
                  <p className="font-medium capitalize">{followUp.patient.skin_type}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p className="font-medium">{followUp.patient.phone}</p>
                </div>
              </div>
              {followUp.patient.allergies && followUp.patient.allergies.length > 0 && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-destructive/5 border border-destructive/20">
                  <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0" />
                  <p className="text-sm">Allergies: {followUp.patient.allergies.join(', ')}</p>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                className="rounded-full mt-2"
                onClick={() => navigate(`/patients/${followUp.patient.id}`)}
                data-testid="view-patient-button"
              >
                View Patient Profile
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Previous Prescription */}
      {prescription && (
        <Card className="border-border shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2 text-primary">
                <Pill className="w-5 h-5" />
                Previous Prescription
              </CardTitle>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4" />
                {formatDate(prescription.created_at)}
                <span>·</span>
                <span>{prescription.doctor_name}</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Morning */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Sun className="w-4 h-4 text-warning" />
                  <span className="text-sm font-medium text-warning">Morning Routine</span>
                </div>
                <MedicineList medicines={prescription.morning_routine?.medicines} label="Medicines" />
                {prescription.morning_routine?.notes && (
                  <p className="text-xs text-muted-foreground mt-2 italic">{prescription.morning_routine.notes}</p>
                )}
                {(!prescription.morning_routine?.medicines || prescription.morning_routine.medicines.length === 0) && (
                  <p className="text-sm text-muted-foreground">No medicines</p>
                )}
              </div>

              {/* Evening */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Moon className="w-4 h-4 text-info" />
                  <span className="text-sm font-medium text-info">Evening Routine</span>
                </div>
                <MedicineList medicines={prescription.evening_routine?.medicines} label="Medicines" />
                {prescription.evening_routine?.notes && (
                  <p className="text-xs text-muted-foreground mt-2 italic">{prescription.evening_routine.notes}</p>
                )}
                {(!prescription.evening_routine?.medicines || prescription.evening_routine.medicines.length === 0) && (
                  <p className="text-sm text-muted-foreground">No medicines</p>
                )}
              </div>

              {/* Oral */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Pill className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-primary">Oral Medicines</span>
                </div>
                <MedicineList medicines={prescription.oral_medicines?.medicines} label="Medicines" />
                {prescription.oral_medicines?.notes && (
                  <p className="text-xs text-muted-foreground mt-2 italic">{prescription.oral_medicines.notes}</p>
                )}
                {(!prescription.oral_medicines?.medicines || prescription.oral_medicines.medicines.length === 0) && (
                  <p className="text-sm text-muted-foreground">No medicines</p>
                )}
              </div>
            </div>

            {/* Procedures & Precautions */}
            {(prescription.procedures || prescription.precautions) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
                {prescription.procedures && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Procedures</p>
                    <p className="text-sm">{prescription.procedures}</p>
                  </div>
                )}
                {prescription.precautions && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Precautions</p>
                    <p className="text-sm">{prescription.precautions}</p>
                  </div>
                )}
              </div>
            )}

            {prescription.follow_up_date && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2 border-t border-border">
                <Calendar className="w-4 h-4" />
                <span>Follow-up was scheduled for: {formatDate(prescription.follow_up_date)}</span>
              </div>
            )}

            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => navigate(`/prescriptions/${prescription.id}/edit`)}
              data-testid="view-prescription-button"
            >
              View Full Prescription
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
